import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";

type CompanyProfile = {
  id: string;
  tenant_id: string;
  company_name: string;
  website: string | null;
  industry: string | null;
  size_range: string | null;
  region: string | null;
  goals: string[];
  pain_points: string[];
  data_sources: string[];
  summary: string;
  enrichment: Record<string, unknown>;
  completeness_pct: number;
  updated_at: string;
};

type FormState = {
  company_name: string;
  website: string;
  industry: string;
  size_range: string;
  region: string;
  goals: string;
  pain_points: string;
  data_sources: string;
  summary: string;
};

const emptyForm: FormState = {
  company_name: "",
  website: "",
  industry: "",
  size_range: "",
  region: "",
  goals: "",
  pain_points: "",
  data_sources: "",
  summary: "",
};

const splitList = (value: string) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const joinList = (items: string[] | undefined) => (items ?? []).join(", ");

function formFromProfile(profile: CompanyProfile): FormState {
  return {
    company_name: profile.company_name ?? "",
    website: profile.website ?? "",
    industry: profile.industry ?? "",
    size_range: profile.size_range ?? "",
    region: profile.region ?? "",
    goals: joinList(profile.goals),
    pain_points: joinList(profile.pain_points),
    data_sources: joinList(profile.data_sources),
    summary: profile.summary ?? "",
  };
}

export default function CompanyProfilePage() {
  const api = useApi();
  const qc = useQueryClient();
  const { effectiveTenantId } = useTenant();
  const [form, setForm] = useState<FormState>(emptyForm);

  const profile = useQuery({
    queryKey: ["company-profile", effectiveTenantId],
    queryFn: () => api.get<CompanyProfile>("/company-profile"),
  });

  useEffect(() => {
    if (profile.data) setForm(formFromProfile(profile.data));
  }, [profile.data]);

  const save = useMutation({
    mutationFn: () =>
      api.put<CompanyProfile>("/company-profile", {
        company_name: form.company_name.trim(),
        website: form.website.trim() || null,
        industry: form.industry.trim() || null,
        size_range: form.size_range.trim() || null,
        region: form.region.trim() || null,
        goals: splitList(form.goals),
        pain_points: splitList(form.pain_points),
        data_sources: splitList(form.data_sources),
        summary: form.summary.trim(),
        enrichment: profile.data?.enrichment ?? {},
      }),
    onSuccess: (updated) => {
      qc.setQueryData(["company-profile", effectiveTenantId], updated);
      qc.invalidateQueries({ queryKey: ["cockpit-summary", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["advisor-proactive", effectiveTenantId] });
    },
  });

  const completeness = profile.data?.completeness_pct ?? 0;
  const isComplete = completeness >= 80;

  return (
    <section className="flow-page company-page">
      <header className="flow-hero">
        <div>
          <p className="hero-eyebrow">Unternehmen</p>
          <h1>Mandantenprofil als Grundlage für KPI-Mining, Benchmarks und Beraterlogik.</h1>
          <p className="hero-sub">
            Dieses Profil steuert Persona, Benchmark-Fokus, Agent-Prioritäten und die KPI-Kandidaten.
            KPI-Zahlen bleiben trotzdem bis Source- und Quality-Gate gesperrt.
          </p>
        </div>
        <div className="flow-score">
          <span>Profilreife</span>
          <strong>{completeness}%</strong>
          <small>{isComplete ? "bereit für Quality Gate" : "Onboarding offen"}</small>
        </div>
      </header>

      {profile.isLoading && <p className="muted">Lade Unternehmensprofil...</p>}
      {profile.error && <p className="error">Unternehmensprofil nicht verfügbar.</p>}

      <div className="company-layout">
        <form
          className="company-form"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <label>
            <span>Unternehmensname *</span>
            <input
              value={form.company_name}
              onChange={(event) => setForm({ ...form, company_name: event.target.value })}
              placeholder="z.B. Acme Mittelstand GmbH"
            />
          </label>
          <label>
            <span>Website</span>
            <input
              value={form.website}
              onChange={(event) => setForm({ ...form, website: event.target.value })}
              placeholder="https://www.example.com"
            />
          </label>
          <div className="company-form-grid">
            <label>
              <span>Branche</span>
              <input
                value={form.industry}
                onChange={(event) => setForm({ ...form, industry: event.target.value })}
                placeholder="B2B Services / Maschinenbau / SaaS"
              />
            </label>
            <label>
              <span>Größe</span>
              <input
                value={form.size_range}
                onChange={(event) => setForm({ ...form, size_range: event.target.value })}
                placeholder="51-200 / 201-500 / 1000+"
              />
            </label>
            <label>
              <span>Region</span>
              <input
                value={form.region}
                onChange={(event) => setForm({ ...form, region: event.target.value })}
                placeholder="DACH / EU / global"
              />
            </label>
          </div>
          <label>
            <span>Ziele, komma-getrennt</span>
            <input
              value={form.goals}
              onChange={(event) => setForm({ ...form, goals: event.target.value })}
              placeholder="growth, margin, cash"
            />
          </label>
          <label>
            <span>Pain Points, komma-getrennt</span>
            <input
              value={form.pain_points}
              onChange={(event) => setForm({ ...form, pain_points: event.target.value })}
              placeholder="manual_reports, late_anomalies, weak_benchmarks"
            />
          </label>
          <label>
            <span>Vorhandene Datenquellen, komma-getrennt</span>
            <input
              value={form.data_sources}
              onChange={(event) => setForm({ ...form, data_sources: event.target.value })}
              placeholder="crm, erp, uploads, crawl4ai"
            />
          </label>
          <label>
            <span>Research-Zusammenfassung</span>
            <textarea
              value={form.summary}
              onChange={(event) => setForm({ ...form, summary: event.target.value })}
              rows={5}
              placeholder="C-Level Kontext, Geschaeftsmodell, Steuerungsbedarf"
            />
          </label>
          <div className="flow-actions">
            <button className="btn primary" type="submit" disabled={save.isPending || !form.company_name.trim()}>
              {save.isPending ? "Speichere..." : "Profil speichern"}
            </button>
            <Link to="/app/onboarding" className="btn ghost">Gefuehrte Einführung</Link>
          </div>
          {save.isSuccess && <p className="success">Profil gespeichert und für Beraterlogik aktualisiert.</p>}
          {save.error && <p className="error">Profil konnte nicht gespeichert werden.</p>}
        </form>

        <aside className="company-side">
          <section className="company-card">
            <h2>Trust Gate</h2>
            <ul className="company-checklist">
              <li className={form.company_name ? "done" : ""}>Unternehmen identifiziert</li>
              <li className={form.industry ? "done" : ""}>Branche bestaetigt</li>
              <li className={form.goals ? "done" : ""}>C-Level Ziele priorisiert</li>
              <li className={form.data_sources ? "done" : ""}>Datenquellen benannt</li>
              <li>Quality Gate separat erforderlich</li>
            </ul>
          </section>
          <section className="company-card">
            <h2>Agenten, die dieses Profil nutzen</h2>
            <div className="agent-chip-list">
              <span>Intake-Agent</span>
              <span>Research-Agent</span>
              <span>Benchmark-Agent</span>
              <span>KPI-Mining-Agent</span>
              <span>Semantic-Mapper</span>
              <span>C-Level Briefing-Agent</span>
            </div>
          </section>
          <section className="company-card">
            <h2>Model Policy</h2>
            <p className="muted small">
              Sensible Tenant-Daten bleiben lokal-first. Crawl4AI, Nimble und OpenRouter werden nur für
              öffentliche oder anonymisierte Research-Signale genutzt.
            </p>
          </section>
        </aside>
      </div>
    </section>
  );
}
